import { useMutation, useQueryClient } from '@tanstack/react-query';

import { QUERY_KEY as USER_PROFILE_QUERY_KEY } from '~/services/useUserProfileQuery';
import { UserProfile, SetupData } from '~/services/types';
import { useHttpClient } from '~/hooks/useHttpClient';
import { sanitizeTagValue } from '~/utils/instrumentation';
import { createLogger } from '~/utils/logging';
import { ClientError } from '~/errors';

const log = createLogger('useSetupMutation');

export const MUTATION_KEY = 'setupMutation';

export type SetupProfileRequest = {
  setupData?: SetupData;
};

export type SetupProfileResponse = {
  teacher: UserProfile;
};

export function useSetupMutation() {
  const queryClient = useQueryClient();
  const httpClient = useHttpClient();

  const { data, error, mutate, reset, status } = useMutation({
    mutationKey: [MUTATION_KEY],
    mutationFn: async (request: SetupProfileRequest) => {
      log.debug('Setting up the teacher', { formData: request.setupData });
      const { teacher } = await httpClient.post<SetupProfileResponse>('/teacher/setup', request.setupData);
      log.info('Set up the teacher successfully', {
        teacherId: teacher.id,
      });
      return { teacher };
    },
    onSuccess: async () => {
      // Invalidate the user profile query and only resolve the setup mutation promise once
      // the user profile query has been refetched (so that user.isSetupComplete is updated).
      await queryClient.invalidateQueries({ queryKey: [USER_PROFILE_QUERY_KEY] });
    },
    onError: (error: Error) => {
      if (error instanceof ClientError) {
        // Ignore client errors.
      } else {
        log.error('Failed to set up the teacher', {
          errorMessage: sanitizeTagValue(error.message),
        });
      }
    },
  });

  return {
    error,
    mutate,
    reset,
    status,
    teacher: data?.teacher,
  };
}
